import { useEffect, useRef, useState } from "react";
import { Camera, Upload, Volume2, ShieldAlert, Loader2, RotateCcw, CameraOff, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Panel, Overline, PageHead, Bar, CAT, C, useLive } from "@/components/kit";

function speak(text, lang) {
  if (!window.speechSynthesis || !text) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  const v = window.speechSynthesis.getVoices().find((x) => x.lang.toLowerCase().startsWith(lang.slice(0, 2)));
  if (v) u.voice = v;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}

function toJpeg(source, w, h) {
  const scale = Math.min(1, 1024 / Math.max(w, h));
  const cv = document.createElement("canvas");
  cv.width = Math.round(w * scale);
  cv.height = Math.round(h * scale);
  cv.getContext("2d").drawImage(source, 0, 0, cv.width, cv.height);
  return cv.toDataURL("image/jpeg", 0.85);
}

function Viewfinder({ videoRef, preview, scanning, camOn }) {
  return (
    <div className="relative aspect-[4/3] xl:aspect-video bg-ink overflow-hidden border border-line grid-bg" data-testid="scanner-viewfinder">
      {preview ? <img src={preview} alt="Captured waste item" className="absolute inset-0 w-full h-full object-cover" />
        : <video ref={videoRef} autoPlay playsInline muted className={`absolute inset-0 w-full h-full object-cover ${camOn ? "" : "hidden"}`} />}
      {!preview && !camOn && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-mute gap-3">
          <CameraOff size={28} strokeWidth={1.5} />
          <span className="font-mono text-xs tracking-[0.2em]">CAMERA OFFLINE / UPLOAD A PHOTO</span>
        </div>
      )}
      <div className="absolute inset-[10%] pointer-events-none">
        {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((c) => (
          <span key={c} className={`absolute w-8 h-8 border-volt ${c}`} />
        ))}
      </div>
      {(scanning || (!preview && camOn)) && (
        <div className="absolute left-[10%] right-[10%] h-[2px] bg-volt scanline" style={{ boxShadow: `0 0 18px 4px ${C.volt}88` }} />
      )}
      <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] bg-ink/80 px-2 py-1 border border-line">
        {scanning ? "CLASSIFYING…" : preview ? "FRAME LOCKED" : camOn ? "LIVE / AIM AT ITEM" : "STANDBY"}
      </div>
    </div>
  );
}

function Result({ r }) {
  const cat = CAT[r.final_category];
  const orig = CAT[r.category];
  return (
    <Panel className="rise" data-testid="scan-result">
      {r.guardrail && (
        <div className="p-5 border-b border-line flex gap-4" style={{ background: `${C.amber}12` }} data-testid="guardrail-banner">
          <ShieldAlert size={20} className="shrink-0" style={{ color: C.amber }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: C.amber }}>{r.contaminated ? "Contamination guardrail intercepted" : "Low-confidence guardrail intercepted"}</div>
            <p className="text-sm text-mute mt-1">{r.guard_en || "Item flagged for contamination."}</p>
            {r.guard_hi && <p className="text-sm text-mute mt-1">{r.guard_hi}</p>}
          </div>
        </div>
      )}
      <div className="p-6">
        <Overline>Classification</Overline>
        <div className="flex items-end justify-between gap-4 mt-3">
          <div>
            <div className="font-display text-4xl font-bold tracking-tight" style={{ color: cat.color }} data-testid="scan-result-category">{cat.label}</div>
            <div className="text-sm text-mute mt-1">{cat.hi} · {cat.bin}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-3xl tabular-nums" data-testid="scan-result-confidence">{Math.round(r.confidence * 100)}%</div>
            <Overline>confidence</Overline>
          </div>
        </div>
        <div className="relative mt-5">
          <Bar value={r.confidence * 100} color={r.confidence >= 0.7 ? cat.color : C.amber} h="h-2" />
          <span className="absolute top-[-4px] h-4 w-px bg-txt" style={{ left: "70%" }} />
          <span className="absolute top-3 font-mono text-[10px] text-mute" style={{ left: "70%", transform: "translateX(-50%)" }}>70% gate</span>
        </div>
        <div className="grid grid-cols-2 gap-px bg-line border border-line mt-10 text-sm">
          {[["Item", r.item], ["Material", r.material || "—"], ["Model call", orig.label], ["Contaminated", r.contaminated ? "Yes" : "No"]].map(([k, v]) => (
            <div key={k} className="bg-surface p-3"><Overline>{k}</Overline><div className="mt-1">{v}</div></div>
          ))}
        </div>
        <p className="text-sm text-mute mt-5">{r.reason}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => speak(r.guard_en || r.bin_en, "en-IN")} data-testid="voice-en-button" className="flex items-center gap-2 border border-line px-4 py-2.5 text-sm hover:bg-raised hover:border-volt/50 transition-colors duration-200">
            <Volume2 size={15} /> English
          </button>
          <button onClick={() => speak(r.guard_hi || r.bin_hi, "hi-IN")} data-testid="voice-hi-button" className="flex items-center gap-2 border border-line px-4 py-2.5 text-sm hover:bg-raised hover:border-volt/50 transition-colors duration-200">
            <Volume2 size={15} /> हिन्दी
          </button>
          <span className="ml-auto font-mono text-sm text-volt self-center" data-testid="scan-credits-earned">{r.credits ? `+${r.credits} eco-credits` : "0 credits"}</span>
        </div>
        {r.credit_note && (
          <div className="mt-5 flex gap-3 border border-non/50 p-4 text-sm" style={{ background: `${C.non}10` }} data-testid="scan-integrity-block">
            <Ban size={16} className="shrink-0 mt-0.5" style={{ color: C.non }} />
            <div><div className="font-semibold" style={{ color: C.non }}>No credits: fair-play check</div><p className="text-mute mt-1" data-testid="scan-credit-note">{r.credit_note}</p></div>
          </div>
        )}
      </div>
    </Panel>
  );
}

function History({ items }) {
  return (
    <Panel data-testid="scan-history">
      <div className="p-5 border-b border-line"><Overline>Your recent scans</Overline></div>
      {items.length === 0 && <div className="p-5 text-sm text-mute">No scans yet. Your first sort earns 10 credits.</div>}
      {items.slice(0, 6).map((s) => (
        <div key={s.id} className="flex items-center justify-between px-5 py-3 border-b border-line last:border-b-0 text-sm">
          <span className="truncate">{s.item}</span>
          <span className="flex items-center gap-3 font-mono text-xs">
            <span className={s.credits ? "text-volt" : "text-mute"}>{s.credits ? `+${s.credits}` : "0"}</span>
            <span style={{ color: CAT[s.final_category].color }}>{CAT[s.final_category].label}</span>
          </span>
        </div>
      ))}
    </Panel>
  );
}

export default function Scanner() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const { data: history, setData: setHistory } = useLive("/scans", 0);

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  };
  useEffect(() => () => stopCam(), []);

  const startCam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = s;
      setPreview(null);
      setResult(null);
      setCamOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 50);
    } catch {
      toast.error("Camera unavailable. Upload a photo instead.");
    }
  };

  const classify = async (dataUrl) => {
    setPreview(dataUrl);
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.post("/scan", { image_base64: dataUrl });
      setResult(data);
      setHistory((h) => [data, ...(h || [])]);
      speak(data.guard_en || data.bin_en, "en-IN");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setScanning(false);
    }
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const url = toJpeg(v, v.videoWidth, v.videoHeight);
    stopCam();
    classify(url);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const img = new Image();
    img.onload = () => { stopCam(); classify(toJpeg(img, img.width, img.height)); };
    img.src = URL.createObjectURL(f);
    e.target.value = "";
  };

  const reset = () => { setPreview(null); setResult(null); };

  return (
    <div data-testid="scanner-page">
      <PageHead kicker="02 / AI smart scanner" title="Point. Sort. Earn." sub="Vision AI classifies the item, a 70% confidence guardrail catches food-soiled packaging, and voice guidance speaks in English and Hindi." />
      <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-4">
        <div className="space-y-4">
          <Viewfinder videoRef={videoRef} preview={preview} scanning={scanning} camOn={camOn} />
          <div className="flex flex-wrap gap-3">
            {camOn ? (
              <button onClick={capture} data-testid="capture-button" className="flex items-center gap-2 bg-volt text-ink font-bold px-6 py-3 hover:bg-[#CCFF00] transition-colors duration-200">
                <Camera size={16} /> Capture & classify
              </button>
            ) : (
              <button onClick={startCam} disabled={scanning} data-testid="start-camera-button" className="flex items-center gap-2 bg-volt text-ink font-bold px-6 py-3 hover:bg-[#CCFF00] transition-colors duration-200 disabled:opacity-60">
                <Camera size={16} /> Open camera
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={scanning} data-testid="upload-photo-button" className="flex items-center gap-2 border border-line px-6 py-3 hover:bg-raised transition-colors duration-200 disabled:opacity-60">
              <Upload size={16} /> Upload photo
            </button>
            {preview && !scanning && (
              <button onClick={reset} data-testid="rescan-button" className="flex items-center gap-2 border border-line px-6 py-3 hover:bg-raised transition-colors duration-200"><RotateCcw size={16} /> New scan</button>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="scanner-file-input" />
          </div>
        </div>
        <div className="space-y-4">
          {scanning && (
            <Panel className="p-8 flex items-center gap-4" data-testid="scan-loading"><Loader2 className="animate-spin text-volt" size={20} />
              <span className="font-mono text-sm">Running vision inference…</span></Panel>
          )}
          {result && <Result r={result} />}
          {!result && !scanning && (
            <Panel className="p-6">
              <Overline>How sorting is judged</Overline>
              <div className="mt-5 space-y-4">
                {Object.entries(CAT).map(([k, c]) => (
                  <div key={k} className="flex items-center gap-4">
                    <span className="w-3 h-10" style={{ background: c.color }} />
                    <div><div className="font-semibold">{c.label} <span className="text-mute font-normal">/ {c.hi}</span></div><div className="text-xs text-mute">{c.bin}</div></div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          <History items={history || []} />
          <Panel className="p-5" data-testid="fair-play-rules">
            <Overline>Fair-play rules</Overline>
            <ul className="mt-4 space-y-2 text-sm text-mute">
              <li>Same or near-identical photo never earns twice (image fingerprinting).</li>
              <li>Screens, printouts and items not being thrown away earn nothing.</li>
              <li>Max 2 rewards per item type and 15 rewarded scans per day, 20s apart.</li>
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
